import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Tabs, Tab, TextField, InputAdornment,
  Pagination, CircularProgress, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, Avatar, Alert, Grid, Paper, Stack,
  MenuItem, Select, FormControl, InputLabel, IconButton, Tooltip,
} from '@mui/material';
import {
  Search, AccountBalance, Print, Close, Payment, TrendingUp,
  TrendingDown, Person, LocalShipping, FilterList, Refresh,
} from '@mui/icons-material';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));

/* ─── Printable Account Statement ──────────────────────────────────────────── */
function AccountStatementContent({ detail, type, tenant }) {
  const txList = type === 'customer' ? detail.ledger : detail.payments;
  const balance = type === 'customer' ? detail.credit_balance : detail.outstanding_balance;
  const balanceLabel = type === 'customer' ? 'Amount Due' : 'Outstanding';
  const today = new Date().toLocaleDateString('en-PK');
  return (
    <Box sx={{ p: 3, color: '#000', fontFamily: 'Arial, sans-serif' }}>
      <style>{`@media print { @page { size: A4; margin: 15mm; } .no-print { display: none !important; } }`}</style>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="primary.main">{tenant?.name || 'Business'}</Typography>
          {tenant?.address && <Typography variant="body2">{tenant.address}</Typography>}
          {tenant?.phone && <Typography variant="body2">Tel: {tenant.phone}</Typography>}
          {tenant?.tax_number && <Typography variant="body2">NTN: {tenant.tax_number}</Typography>}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#1e40af' }}>ACCOUNT STATEMENT</Typography>
          <Typography variant="body2" fontWeight={600}>{type === 'customer' ? 'Customer' : 'Supplier'}: {detail.name}</Typography>
          {detail.phone && <Typography variant="body2">{detail.phone}</Typography>}
          {detail.address && <Typography variant="body2">{detail.address}</Typography>}
          <Typography variant="body2" color="text.secondary">As of: {today}</Typography>
        </Box>
      </Box>
      <Divider sx={{ borderWidth: 2, borderColor: '#1e40af', mb: 2 }} />
      <Box sx={{ mb: 2, p: 1.5, bgcolor: balance > 0 ? '#fef2f2' : '#f0fdf4', border: '1px solid', borderColor: balance > 0 ? '#fca5a5' : '#86efac', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography fontWeight={700} color={balance > 0 ? 'error.main' : 'success.main'} variant="h6">
          {balanceLabel}: ₨ {fmt(balance)}
        </Typography>
        <Chip label={balance > 0 ? 'OUTSTANDING' : 'SETTLED'} color={balance > 0 ? 'error' : 'success'} sx={{ fontWeight: 700 }} />
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#1e40af' }}>
            <TableCell sx={{ color: 'white', fontWeight: 700 }}>#</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700 }}>Date</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700 }}>Description</TableCell>
            <TableCell align="right" sx={{ color: 'white', fontWeight: 700 }}>Debit (Dr)</TableCell>
            <TableCell align="right" sx={{ color: 'white', fontWeight: 700 }}>Credit (Cr)</TableCell>
            <TableCell align="right" sx={{ color: 'white', fontWeight: 700 }}>Balance</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(txList || []).map((tx, i) => (
            <TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: '#f8fafc' } }}>
              <TableCell>{i + 1}</TableCell>
              <TableCell>{new Date(tx.entry_date || tx.created_at).toLocaleDateString('en-PK')}</TableCell>
              <TableCell>{tx.description || tx.notes || '—'}</TableCell>
              <TableCell align="right" sx={{ color: tx.type === 'debit' ? '#dc2626' : 'inherit' }}>
                {tx.type === 'debit' ? `₨ ${fmt(tx.amount)}` : '—'}
              </TableCell>
              <TableCell align="right" sx={{ color: tx.type === 'credit' ? '#16a34a' : 'inherit' }}>
                {tx.type === 'credit' ? `₨ ${fmt(tx.amount)}` : '—'}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>₨ {fmt(tx.balance_after)}</TableCell>
            </TableRow>
          ))}
          {(!txList || !txList.length) && (
            <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#94a3b8' }}>No transactions found</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      <Box sx={{ mt: 3, pt: 2, borderTop: '2px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">Generated: {new Date().toLocaleString('en-PK')} — POS Pro Pakistan</Typography>
        <Typography fontWeight={700} color={balance > 0 ? 'error.main' : 'success.main'}>Closing Balance: ₨ {fmt(balance)}</Typography>
      </Box>
    </Box>
  );
}

/* ─── Account Statement Dialog ──────────────────────────────────────────────── */
function AccountStatementDialog({ party, type, tenant, open, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const printRef = useRef();
  const handlePrint = useReactToPrint({ contentRef: printRef });

  useEffect(() => {
    if (!open || !party) return;
    setLoading(true); setDetail(null);
    const endpoint = type === 'customer' ? `/customers/${party.id}` : `/suppliers/${party.id}`;
    api.get(endpoint).then(r => setDetail(r.data.data)).catch(() => {}).finally(() => setLoading(false));
  }, [open, party, type]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalance color="primary" />
          Account Statement — {party?.name}
        </Box>
        <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
        {detail && <Box ref={printRef}><AccountStatementContent detail={detail} type={type} tenant={tenant} /></Box>}
      </DialogContent>
      {detail && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose}>Close</Button>
          <Button variant="contained" startIcon={<Print />} onClick={handlePrint}>Print Statement</Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

/* ─── Quick Payment Dialog ──────────────────────────────────────────────────── */
function QuickPayDialog({ party, type, open, onClose, onPaid }) {
  const dispatch = useDispatch();
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setAmount(''); setMethod('cash'); setNote(''); } }, [open]);

  const save = async () => {
    if (!amount || +amount <= 0) return;
    setSaving(true);
    try {
      const endpoint = type === 'customer' ? `/customers/${party.id}/payment` : `/suppliers/${party.id}/payment`;
      await api.post(endpoint, { amount: +amount, method, description: note || (type === 'customer' ? 'Payment received' : 'Payment to supplier') });
      dispatch(showSnackbar({ message: 'Payment recorded successfully', severity: 'success' }));
      onPaid();
      onClose();
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Failed', severity: 'error' }));
    } finally { setSaving(false); }
  };

  const maxDue = type === 'customer' ? party?.credit_balance : party?.outstanding_balance;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <Payment color="success" />
        Record Payment — {party?.name}
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Outstanding: <strong>₨ {fmt(maxDue)}</strong>
        </Alert>
        <Stack spacing={2}>
          <TextField
            label="Amount (₨)" type="number" size="small" fullWidth autoFocus
            value={amount} onChange={e => setAmount(e.target.value)}
            inputProps={{ min: 1, max: maxDue }}
            helperText={`Max: ₨ ${fmt(maxDue)}`}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Payment Method</InputLabel>
            <Select value={method} onChange={e => setMethod(e.target.value)} label="Payment Method">
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              <MenuItem value="cheque">Cheque</MenuItem>
              <MenuItem value="card">Card</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Note (optional)" size="small" fullWidth value={note} onChange={e => setNote(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="success" onClick={save} disabled={saving || !amount || +amount <= 0}>
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─── Party Ledger List Tab ─────────────────────────────────────────────────── */
function PartyLedger({ type, tenant, onSummaryChange }) {
  const [parties, setParties] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterDue, setFilterDue] = useState('all');
  const [loading, setLoading] = useState(false);
  const [stmtParty, setStmtParty] = useState(null);
  const [payParty, setPayParty] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const endpoint = type === 'customer' ? '/customers' : '/suppliers';
    try {
      const { data } = await api.get(endpoint, { params: { page, limit: 20, search } });
      setParties(data.data); setTotal(data.pagination?.total || 0);
    } finally { setLoading(false); }
  }, [type, page, search]);

  useEffect(() => { load(); }, [load]);

  const balanceField = type === 'customer' ? 'credit_balance' : 'outstanding_balance';

  const filtered = filterDue === 'due'
    ? parties.filter(p => p[balanceField] > 0)
    : filterDue === 'clear'
    ? parties.filter(p => !p[balanceField] || p[balanceField] <= 0)
    : parties;

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search name / phone…" size="small" sx={{ minWidth: 240 }}
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Filter</InputLabel>
          <Select value={filterDue} onChange={e => setFilterDue(e.target.value)} label="Filter">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="due">With Balance</MenuItem>
            <MenuItem value="clear">Settled</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>City</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Outstanding Balance</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No records found</TableCell></TableRow>
            )}
            {filtered.map(p => (
              <TableRow key={p.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 30, height: 30, fontSize: 13, bgcolor: type === 'customer' ? '#6366f1' : '#f59e0b' }}>
                      {p.name?.[0]}
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>{p.phone || '—'}</TableCell>
                <TableCell>{p.city || '—'}</TableCell>
                <TableCell align="right">
                  <Typography fontWeight={700} fontSize={15} color={p[balanceField] > 0 ? 'error.main' : 'success.main'}>
                    ₨ {fmt(p[balanceField])}
                  </Typography>
                </TableCell>
                <TableCell>
                  {p[balanceField] > 0
                    ? <Chip label="Due" size="small" color="error" />
                    : <Chip label="Clear" size="small" color="success" />}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Button size="small" variant="outlined" startIcon={<Print />}
                      onClick={() => setStmtParty(p)}>
                      Statement
                    </Button>
                    {p[balanceField] > 0 && (
                      <Button size="small" variant="contained" color="success" startIcon={<Payment />}
                        onClick={() => setPayParty(p)}>
                        Pay
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Pagination count={Math.ceil(total / 20)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
      </Box>

      <AccountStatementDialog open={!!stmtParty} party={stmtParty} type={type} tenant={tenant} onClose={() => setStmtParty(null)} />
      <QuickPayDialog open={!!payParty} party={payParty} type={type} onClose={() => setPayParty(null)} onPaid={() => { load(); onSummaryChange(); }} />
    </Box>
  );
}

/* ─── Transactions Journal Tab ──────────────────────────────────────────────── */
function JournalTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [partyType, setPartyType] = useState('all');
  const [entryType, setEntryType] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/ledger/journal', {
        params: { party_type: partyType === 'all' ? '' : partyType, type: entryType === 'all' ? '' : entryType, from, to, page, limit: LIMIT }
      });
      setEntries(data.data?.rows || []); setTotal(data.data?.total || 0);
    } finally { setLoading(false); }
  }, [partyType, entryType, from, to, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Party Type</InputLabel>
          <Select value={partyType} onChange={e => { setPartyType(e.target.value); setPage(1); }} label="Party Type">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="customer">Customers</MenuItem>
            <MenuItem value="supplier">Suppliers</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Entry Type</InputLabel>
          <Select value={entryType} onChange={e => { setEntryType(e.target.value); setPage(1); }} label="Entry Type">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="debit">Debit (Dr)</MenuItem>
            <MenuItem value="credit">Credit (Cr)</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" label="From" type="date" value={from} onChange={e => { setFrom(e.target.value); setPage(1); }} InputLabelProps={{ shrink: true }} />
        <TextField size="small" label="To"   type="date" value={to}   onChange={e => { setTo(e.target.value); setPage(1); }}   InputLabelProps={{ shrink: true }} />
        <Button variant="contained" size="small" onClick={load} disabled={loading}>Apply</Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
      ) : (
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Party</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>Debit (Dr)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>Credit (Cr)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Balance After</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No entries found</TableCell></TableRow>
            )}
            {entries.map((e, i) => (
              <TableRow key={i} hover sx={{ bgcolor: e.type === 'debit' ? '#fff9f9' : '#f9fff9' }}>
                <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{new Date(e.entry_date || e.created_at).toLocaleDateString('en-PK')}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    {e.party_type === 'customer' ? <Person sx={{ fontSize: 14, color: '#6366f1' }} /> : <LocalShipping sx={{ fontSize: 14, color: '#f59e0b' }} />}
                    <Typography variant="body2" fontWeight={600}>{e.party_name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={e.party_type} size="small" color={e.party_type === 'customer' ? 'primary' : 'warning'} variant="outlined" />
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>{e.description || '—'}</TableCell>
                <TableCell align="right" sx={{ color: 'error.main', fontWeight: e.type === 'debit' ? 700 : 400 }}>
                  {e.type === 'debit' ? `₨ ${fmt(e.amount)}` : '—'}
                </TableCell>
                <TableCell align="right" sx={{ color: 'success.main', fontWeight: e.type === 'credit' ? 700 : 400 }}>
                  {e.type === 'credit' ? `₨ ${fmt(e.amount)}` : '—'}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>₨ {fmt(e.balance_after)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Pagination count={Math.ceil(total / LIMIT)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
      </Box>
    </Box>
  );
}

/* ─── Summary Cards ─────────────────────────────────────────────────────────── */
function SummaryCards({ summary }) {
  if (!summary) return null;
  const net = summary.totalCustomerDue - summary.totalSupplierDue;
  return (
    <Grid container spacing={2} mb={3}>
      <Grid item xs={6} md={3}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', borderLeft: '4px solid #ef4444' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <TrendingUp sx={{ color: '#ef4444', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">Customer Receivables</Typography>
          </Box>
          <Typography variant="h5" fontWeight={800} color="error.main">₨ {fmt(summary.totalCustomerDue)}</Typography>
          <Typography variant="caption" color="text.secondary">{summary.customerCount} customers with balance</Typography>
        </Paper>
      </Grid>
      <Grid item xs={6} md={3}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', borderLeft: '4px solid #f59e0b' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <TrendingDown sx={{ color: '#f59e0b', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">Supplier Payables</Typography>
          </Box>
          <Typography variant="h5" fontWeight={800} color="warning.main">₨ {fmt(summary.totalSupplierDue)}</Typography>
          <Typography variant="caption" color="text.secondary">{summary.supplierCount} suppliers with balance</Typography>
        </Paper>
      </Grid>
      <Grid item xs={6} md={3}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', borderLeft: `4px solid ${net >= 0 ? '#22c55e' : '#ef4444'}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <AccountBalance sx={{ color: net >= 0 ? '#22c55e' : '#ef4444', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">Net Receivable</Typography>
          </Box>
          <Typography variant="h5" fontWeight={800} color={net >= 0 ? 'success.main' : 'error.main'}>₨ {fmt(Math.abs(net))}</Typography>
          <Typography variant="caption" color="text.secondary">{net >= 0 ? 'You are owed more' : 'You owe more'}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={6} md={3}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', borderLeft: '4px solid #6366f1' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <FilterList sx={{ color: '#6366f1', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">Total Parties w/ Balance</Typography>
          </Box>
          <Typography variant="h5" fontWeight={800} color="#6366f1">{summary.customerCount + summary.supplierCount}</Typography>
          <Typography variant="caption" color="text.secondary">Customers + Suppliers combined</Typography>
        </Paper>
      </Grid>
    </Grid>
  );
}

/* ─── Main Ledger Page ──────────────────────────────────────────────────────── */
export default function LedgerPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState(null);
  const [tenant, setTenant] = useState(null);

  const loadSummary = useCallback(() => {
    Promise.all([
      api.get('/customers?limit=500'),
      api.get('/suppliers?limit=500'),
    ]).then(([custRes, supRes]) => {
      const customers = custRes.data.data || [];
      const suppliers = supRes.data.data || [];
      setSummary({
        totalCustomerDue: customers.reduce((s, c) => s + (+c.credit_balance || 0), 0),
        customerCount: customers.filter(c => c.credit_balance > 0).length,
        totalSupplierDue: suppliers.reduce((s, s2) => s + (+s2.outstanding_balance || 0), 0),
        supplierCount: suppliers.filter(s => s.outstanding_balance > 0).length,
      });
    });
    api.get('/settings/business').then(r => setTenant(r.data.data)).catch(() => {});
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <AccountBalance color="primary" sx={{ fontSize: 28 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>{t('nav.ledger')} / کھاتہ بہی</Typography>
          <Typography variant="caption" color="text.secondary">Customer & Supplier account management</Typography>
        </Box>
      </Box>

      <SummaryCards summary={summary} />

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Customer Ledger" icon={<Person sx={{ fontSize: 16 }} />} iconPosition="start" />
          <Tab label="Supplier Ledger" icon={<LocalShipping sx={{ fontSize: 16 }} />} iconPosition="start" />
          <Tab label="Transaction Journal" icon={<AccountBalance sx={{ fontSize: 16 }} />} iconPosition="start" />
        </Tabs>
        <CardContent>
          {tab === 0 && <PartyLedger type="customer" tenant={tenant} onSummaryChange={loadSummary} />}
          {tab === 1 && <PartyLedger type="supplier" tenant={tenant} onSummaryChange={loadSummary} />}
          {tab === 2 && <JournalTab />}
        </CardContent>
      </Card>
    </Box>
  );
}
